import { useEffect, useState } from "react";
import { UserPlus, Trash2, Shield, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const emptyNew = { email: "", password: "", name: "", permissions: [] };

export default function AdminsAdmin() {
  const [features, setFeatures] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyNew);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [editPwd, setEditPwd] = useState("");

  const load = () => Promise.all([api.get("/admin/features"), api.get("/admin/admins")])
    .then(([f, a]) => { setFeatures(f.data); setRows(a.data); });
  useEffect(() => { load(); }, []);

  const toggle = (list, key) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const create = async () => {
    if (!form.email || !form.password) return toast.error("Email and password are required");
    setBusy(true);
    try { await api.post("/admin/admins", form); toast.success("Sub-admin created"); setForm(emptyNew); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const startEdit = (a) => { setEditing(a.user_id); setEditPerms(a.permissions || []); setEditPwd(""); };
  const saveEdit = async (a) => {
    try { await api.put(`/admin/admins/${a.user_id}`, { permissions: editPerms, password: editPwd || undefined }); toast.success("Updated"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (a) => { if (!window.confirm(`Remove ${a.email}?`)) return; try { await api.delete(`/admin/admins/${a.user_id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="max-w-4xl">
      <h1 className="font-display font-extrabold text-3xl mb-1 flex items-center gap-2"><Shield className="h-6 w-6 text-violet-400" /> Admins & access</h1>
      <p className="text-slate-400 mb-6">Create sub-admins and choose exactly which sections each one can manage. Only the super admin sees this page.</p>

      <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5 mb-6">
        <div className="font-semibold mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4 text-violet-400" /> New sub-admin</div>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <Input placeholder="Email*" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email" className="bg-slate-950/50 border-white/10" />
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-admin-name" className="bg-slate-950/50 border-white/10" />
          <Input placeholder="Password*" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password" className="bg-slate-950/50 border-white/10" />
        </div>
        <div className="text-xs text-slate-400 mb-2">Access rights</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {features.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-white/10 px-3 py-2 hover:bg-white/5">
              <Checkbox checked={form.permissions.includes(f.key)} onCheckedChange={() => setForm({ ...form, permissions: toggle(form.permissions, f.key) })} data-testid={`new-perm-${f.key}`} />
              {f.label}
            </label>
          ))}
        </div>
        <Button onClick={create} disabled={busy} data-testid="create-admin-btn" className="bg-violet-600 hover:bg-violet-500">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />} Create sub-admin
        </Button>
      </div>

      <div className="space-y-3">
        {rows.map((a) => (
          <div key={a.user_id} className="rounded-2xl border border-white/10 bg-[#131a29] p-4" data-testid={`admin-row-${a.email}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold">{a.name || a.email} {a.is_super && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">Super admin</span>}</div>
                <div className="text-sm text-slate-400">{a.email}</div>
              </div>
              {!a.is_super && (
                <div className="flex gap-2">
                  {editing === a.user_id
                    ? (<><Button size="sm" onClick={() => saveEdit(a)} data-testid={`save-admin-${a.email}`} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"><Save className="h-4 w-4" /></Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button></>)
                    : <Button size="sm" variant="secondary" onClick={() => startEdit(a)} data-testid={`edit-admin-${a.email}`}>Edit access</Button>}
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(a)} data-testid={`del-admin-${a.email}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {a.is_super
                ? <span className="text-xs text-slate-400">Full access to everything</span>
                : editing === a.user_id
                  ? features.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-xs cursor-pointer rounded-lg border border-white/10 px-2.5 py-1.5 hover:bg-white/5">
                        <Checkbox checked={editPerms.includes(f.key)} onCheckedChange={() => setEditPerms(toggle(editPerms, f.key))} data-testid={`edit-perm-${a.email}-${f.key}`} /> {f.label}
                      </label>))
                  : (a.permissions.length ? a.permissions.map((p) => <span key={p} className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10">{(features.find((f) => f.key === p) || {}).label || p}</span>) : <span className="text-xs text-slate-500">No access assigned yet</span>)}
            </div>
            {editing === a.user_id && (
              <div className="mt-3"><Input placeholder="Reset password (optional)" type="password" value={editPwd} onChange={(e) => setEditPwd(e.target.value)} data-testid={`reset-pwd-${a.email}`} className="bg-slate-950/50 border-white/10 max-w-xs" /></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
